import { Composer, type ComposerAttachment, MenuOption, useToast } from "@design";
import { Bot, Cloud, HardDrive, Sparkle, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import type { AgentSummary, BrandSummary, ContextInfoData, ReasoningConfig, ReasoningEffort } from "@/api";
import { useComposerLabels } from "@/components/designLabels";
import { InlineNotice } from "@/components/InlineNotice";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { MoreMenu } from "./BrandMenu";
import { ChatContextUsage } from "./ChatContextUsage";
import { CloudFilesDialog, type CloudSource } from "./CloudFilesDialog";
import { AssistantMenu } from "./ComposerMenus";
import { SharePointFolderDialog } from "./SharePointFolderDialog";
import { TierMenu } from "./TierMenu";
import type { CloudFile, FolderLink } from "./useAttachments";
import type { ChatModelOption } from "./useChatModels";
import { isCreatedChat, useChatSwitchKey } from "./useChatSwitchKey";
import { useDictation } from "./voice/useDictation";

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** A reply is streaming or a send is in flight. */
  busy: boolean;
  /** A reply is being written and can be stopped: the send button turns into Stop. */
  generating?: boolean;
  onStop?: () => void;
  /** True once the chat exists: assistant and model are then fixed. */
  started: boolean;
  agents: AgentSummary[];
  agentUuid: string | null;
  agentName?: string | null;
  onAgentChange: (agentUuid: string | null) => void;
  modelOptions: ChatModelOption[];
  modelId: string | null;
  modelLabel: string;
  onModelChange: (id: string) => void;
  reasoning: ReasoningConfig | null;
  effort: ReasoningEffort | null;
  onEffortChange: (effort: ReasoningEffort | null) => void;
  noModel: boolean;
  attachments: ComposerAttachment[];
  /** Warnings about attached files (their checks can't run or be read now), shown in full above the composer. */
  attachmentNotices?: string[];
  /** Files are still uploading; sending waits for them. */
  uploading: boolean;
  onAttach: (files: File[]) => void;
  /** The file picker's `accept` filter: the types the backend takes. */
  accept?: string;
  onAttachCloud: (files: CloudFile[]) => void;
  /** A pasted SharePoint folder link whose files are being offered. */
  folderLink?: FolderLink | null;
  onRemoveAttachment: (key: string) => void;
  /** Replaces the default "Message <agent>" placeholder. */
  placeholder?: string;
  /** No agent picker (Build with AI chats always use AnthmAI). */
  hideAssistant?: boolean;
  /** The last reply's context-window report; shown only when the user turned on "Show context usage". */
  context?: ContextInfoData | null;
  /** The chat's brand picker ("More options"); omitted when fewer than two brands are offered. */
  brand?: {
    brands: BrandSummary[];
    selected: string | null;
    onSelect: (brandUuid: string | null) => void;
    /** Set once the chat has started (null = the default brand). */
    fixedName?: string | null;
  };
  /** The open chat. Dictation stops when the user moves to another one. */
  sessionUuid?: string;
}

export function ChatComposer(props: Readonly<ChatComposerProps>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const labels = useComposerLabels();
  const fileInput = useRef<HTMLInputElement>(null);
  const capabilities = useCapabilities();
  const toast = useToast();
  const location = useLocation();
  const chatKey = useChatSwitchKey(props.sessionUuid, isCreatedChat(location.state));
  const dictation = useDictation({
    value: props.value,
    onChange: props.onChange,
    resetKey: chatKey,
    onError: (error) => toast.error(t(`voice.${error}`)),
  });
  const listening = dictation.status === "recording" || dictation.status === "finishing";
  const [cloud, setCloud] = useState<CloudSource | null>(null);
  const { started, agentUuid, effort } = props;
  const agentLabel = props.agentName ?? props.agents.find((a) => a.uuid === agentUuid)?.name ?? t("defaultAssistant");
  // An agent answers with its own model, so only thinking applies.
  const pickModel = !started && !agentUuid;
  const tierLabel = effort
    ? t("tierWithEffort", { tier: props.modelLabel, effort: t(`effort.${effort}`) })
    : props.modelLabel;
  const showTier = pickModel || Boolean(props.modelLabel);

  return (
    <div className="border-t border-border-subtle bg-surface-primary px-4 py-3">
      <div className="mx-auto max-w-[720px]">
        {props.attachmentNotices && props.attachmentNotices.length > 0 && (
          <div className="mb-2 flex flex-col gap-2">
            {props.attachmentNotices.map((notice) => (
              <InlineNotice key={notice} tone="warning">
                {notice}
              </InlineNotice>
            ))}
          </div>
        )}
        <Composer
          placeholder={props.placeholder ?? t("placeholder", { name: agentLabel })}
          labels={labels}
          showPosture={false}
          value={props.value}
          onChange={props.onChange}
          onSend={props.onSend}
          generating={props.generating}
          onStop={props.onStop}
          sendDisabled={props.busy || props.uploading || props.noModel || listening || props.value.trim().length === 0}
          onMicClick={capabilities.transcriptionEnabled || dictation.status !== "idle" ? dictation.toggle : undefined}
          recording={dictation.status === "recording"}
          // Stop ignores micDisabled, so a recording can always be ended.
          micDisabled={props.busy || props.uploading || props.noModel || dictation.status !== "idle"}
          attachments={props.attachments}
          onRemoveAttachment={(attachment) => props.onRemoveAttachment(attachment.key)}
          onFilesPasted={props.onAttach}
          attachMenu={
            <>
              <MenuOption icon={<Upload size={16} />} onSelect={() => fileInput.current?.click()}>
                {t("attachFile")}
              </MenuOption>
              {capabilities.sharepointEnabled && (
                <MenuOption icon={<Cloud size={16} />} onSelect={() => setCloud("sharepoint")}>
                  {t("attachSharePoint")}
                </MenuOption>
              )}
              {capabilities.googleDriveEnabled && (
                <MenuOption icon={<HardDrive size={16} />} onSelect={() => setCloud("googleDrive")}>
                  {t("attachGoogleDrive")}
                </MenuOption>
              )}
            </>
          }
          assistantLabel={props.hideAssistant ? undefined : agentLabel}
          assistantIcon={agentUuid ? <Bot size={14} /> : <Sparkle size={14} />}
          assistantMenu={
            props.hideAssistant ? undefined : (
              <AssistantMenu
                agents={props.agents}
                selected={agentUuid}
                onSelect={props.onAgentChange}
                fixedName={started ? agentLabel : undefined}
              />
            )
          }
          moreMenu={
            props.brand && (
              <MoreMenu
                brands={props.brand.brands}
                selected={props.brand.selected}
                onSelect={props.brand.onSelect}
                fixedBrandName={props.brand.fixedName}
              />
            )
          }
          tierLabel={tierLabel || undefined}
          tierMenu={
            showTier ? (
              <TierMenu
                options={pickModel ? props.modelOptions : undefined}
                fixedModel={pickModel ? undefined : props.modelLabel}
                selectedId={props.modelId}
                onSelect={props.onModelChange}
                reasoning={props.reasoning}
                effort={effort}
                onEffortChange={props.onEffortChange}
              />
            ) : undefined
          }
        />
        {props.context && (
          <div className="mt-2">
            <ChatContextUsage info={props.context} />
          </div>
        )}
        <CloudFilesDialog source={cloud} onClose={() => setCloud(null)} onAttach={props.onAttachCloud} />
        <SharePointFolderDialog link={props.folderLink ?? null} onAttach={props.onAttachCloud} />
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          accept={props.accept}
          aria-label={t("attachFile")}
          onChange={(event) => {
            props.onAttach(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
        <div className="mt-2 text-center">
          <Text variant="caption" tone="tertiary">
            {props.noModel ? t("noModel") : t("disclaimer")}
          </Text>
        </div>
      </div>
    </div>
  );
}
